import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SearchDropdown from '../../../components/feature/SearchDropdown';
import { allTools } from '../../../mocks/tools';

export default function Hero() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    setShowSearchDropdown(value.trim().length >= 2);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchDropdown(false);
      setSearchQuery('');
    }
  };

  const handleCloseDropdown = () => {
    setShowSearchDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExploreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const element = document.getElementById('tools-directory');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ paddingBottom: 0 }}>
      {/* Background */}
      <div className="absolute inset-0 w-full h-full bg-[#0a0a0a]">
        <img
          src="https://public.readdy.ai/ai/img_res/e4e790b6afc6785467f787e554d1548a.jpg"
          alt="AI technology background with neural network connections"
          width={1920}
          height={1080}
          fetchPriority="high"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/50 to-[#0a0a0a]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_40%,rgba(0,176,155,0.07),transparent)]"></div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 w-full pointer-events-none"
        style={{ height: '150px', background: 'linear-gradient(to bottom, transparent, #0a0a0a)', zIndex: 5 }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-44 flex flex-col items-center text-center">

        {/* Eyebrow label */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/25 text-teal-400 text-xs font-semibold tracking-widest uppercase mb-8 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse"></span>
          AI Tools Directory
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-[1.08] tracking-[-0.02em] px-2">
          Discover the Best{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-cyan-300 to-teal-400">
            AI Tools
          </span>
          <br />
          for Work, Creativity &amp; Business
        </h1>

        {/* Subheadline */}
        <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed font-normal px-4">
          Toolnova helps creators, entrepreneurs, and businesses quickly find powerful AI tools through curated recommendations and organized categories.
        </p>

        {/* Hero Search Bar */}
        <div className="w-full max-w-2xl mb-10 px-4 sm:px-0 relative" ref={searchRef}>
          <form onSubmit={handleSearchSubmit}>
            <div className="relative flex items-center">
              <div className="w-6 h-6 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <i className="ri-search-line text-xl text-teal-400"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                placeholder="Search for AI tools, e.g. &quot;video editing&quot; or &quot;ChatGPT&quot;…"
                className="w-full pl-12 pr-28 py-4 bg-white/5 border border-white/15 hover:border-teal-500/40 focus:border-teal-500 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/25 transition-all duration-300 backdrop-blur-sm cursor-text"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-teal-500/30 hover:scale-105 active:scale-95"
              >
                Search
              </button>
            </div>
          </form>
          {/* Suggestions */}
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            {['ChatGPT', 'Midjourney', 'Runway', 'GitHub Copilot', 'Sora'].map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  navigate(`/search?q=${encodeURIComponent(tag)}`);
                }}
                className="px-3 py-1 rounded-full bg-white/5 border border-white/10 hover:border-teal-500/40 text-gray-400 hover:text-teal-400 text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {tag}
              </button>
            ))}
          </div>
          {/* Search Dropdown */}
          <SearchDropdown
            query={searchQuery}
            onClose={handleCloseDropdown}
            isOpen={showSearchDropdown}
          />
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center w-full sm:w-auto px-4 sm:px-0">
          <a
            href="#tools-directory"
            onClick={handleExploreClick}
            className="group relative inline-flex items-center justify-center gap-2.5 px-9 py-4 bg-teal-500 hover:bg-teal-400 text-white text-base font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer shadow-2xl shadow-teal-500/40 hover:shadow-teal-400/60 hover:scale-105 active:scale-95"
          >
            Explore AI Tools
            <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
          </a>
          <Link
            to="/quiz"
            className="group inline-flex items-center justify-center gap-2.5 px-9 py-4 bg-transparent hover:bg-teal-500/10 text-teal-400 hover:text-teal-300 text-base font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer border-2 border-teal-500/70 hover:border-teal-400 hover:scale-105 active:scale-95"
          >
            <i className="ri-flashlight-line text-lg group-hover:rotate-12 transition-transform duration-300"></i>
            Take the Quiz
            <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link>
        </div>

        {/* Stats row */}
        <div className="mt-16 flex items-center justify-center gap-0">
          {[
            { value: `${allTools.length}+`, label: 'Tools Curated' },
            { value: '10',   label: 'Categories' },
            { value: 'Weekly', label: 'Updated' },
          ].map((stat, idx) => (
            <div key={stat.label} className="flex items-center">
              <div className="flex flex-col items-center gap-1 px-8">
                <span className="text-xl sm:text-2xl font-bold text-white">{stat.value}</span>
                <span className="text-xs text-gray-500 tracking-wide uppercase">{stat.label}</span>
              </div>
              {idx < 2 && (
                <div className="w-px h-8 bg-gray-700/60 shrink-0"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-xs text-gray-600 tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-xl text-teal-500/70"></i>
      </div>
    </section>
  );
}
